import { gql, useLazyQuery, ApolloError } from "@apollo/client";
import {
  Error,
  VerifyNjoyCodeData,
  VerifyNjoyCodeQueryQuery,
  VerifyNjoyCodeQueryQueryVariables,
} from "../../../../schemaTypes";

export const VERIFY_NJOY_CODE_QUERY = gql`
  query VerifyNjoyCodeQuery($njoyCode: String!) {
    verifyNjoyCode(njoyCode: $njoyCode) {
      errors {
        path
        message
      }
      message
      data {
        offerTitle
        offerType
        offerCode
        status
        redeemedDate
        usedDate
      }
    }
  }
`;

interface SubmitResponse {
  errors: { path: string; message: string }[] | null;
  message: string | null;
  data: VerifyNjoyCodeData | null;
}

const mapVerifyNjoyCodeApolloError = (apolloError: ApolloError): SubmitResponse => {
  const graphQLErrors = apolloError.graphQLErrors
    ?.filter(
      (
        graphQLError
      ): graphQLError is { message: string; path?: readonly (string | number)[] } =>
        Boolean(graphQLError?.message)
    )
    .map(({ message, path }) => ({
      path:
        path
          ?.filter(
            (pathPart): pathPart is string => typeof pathPart === "string"
          )
          .join(".") || "general",
      message,
    }));

  if (graphQLErrors && graphQLErrors.length > 0) {
    return {
      errors: graphQLErrors,
      message: graphQLErrors[0].message,
      data: null,
    };
  }

  return {
    errors: [{ path: "general", message: apolloError.message }],
    message: apolloError.message,
    data: null,
  };
};

interface Props {
  children: (data: {
    submit: (
      values: VerifyNjoyCodeQueryQueryVariables
    ) => Promise<SubmitResponse | null>;
    loading: boolean;
    error?: Error | ApolloError;
  }) => React.ReactElement | null;
}

export const VerifyNjoyCodeController: React.FC<Props> = ({ children }) => {
  const [verifyNjoyCode, { loading, error }] = useLazyQuery<
    VerifyNjoyCodeQueryQuery,
    VerifyNjoyCodeQueryQueryVariables
  >(VERIFY_NJOY_CODE_QUERY, {
    fetchPolicy: "no-cache",
  });

  const submit = async (
    values: VerifyNjoyCodeQueryQueryVariables
  ): Promise<SubmitResponse | null> => {
    try {
      const { data } = await verifyNjoyCode({ variables: values });

      if (!data?.verifyNjoyCode) return null;

      return {
        errors: data.verifyNjoyCode.errors
          ? data.verifyNjoyCode.errors
              .filter(
                (e): e is { path: string; message: string } => e !== null
              )
              .map(({ path, message }) => ({ path, message }))
          : null,
        message: data.verifyNjoyCode.message ?? null,
        data: data.verifyNjoyCode.data ?? null,
      };
    } catch (queryError) {
      return mapVerifyNjoyCodeApolloError(queryError as ApolloError);
    }
  };

  return children({ submit, loading, error });
};
